/** Brand colors for the BNSN logo */
const BRAND_BLUE = '#4457f3'
const BRAND_DARK = '#201914'

/**
 * BNSN Color Logo with Text - Brand logo atom component
 *
 * The primary BNSN brand logo featuring the distinctive brain icon with
 * accompanying text in horizontal layout. Supports collapsible mode where
 * only the brain icon is shown, making it perfect for responsive sidebars.
 *
 * Features:
 * - Responsive logo that adapts to available space
 * - Collapsible text for compact displays
 * - Color-coded brain elements (blue and orange)
 * - Consistent branding across all applications
 *
 * @param classNameName - Optional CSS className name for styling
 * @param height - Height of the logo in pixels (default: 40)
 * @param width - Width of the logo in pixels (auto-calculated when showText=false)
 * @param showText - Whether to display the "BNSN" text portion (default: true)
 * @returns JSX.Element - The BNSN brand logo SVG component
 */

/** Props interface for the BNSN logo component, extending SVG props but controlling dimensions */
interface BnsnLogoColorWithTextProps extends Omit<React.SVGProps<SVGSVGElement>, 'width' | 'height'> {
  /** Height of the logo in pixels */
  height?: number
  /** Width of the logo in pixels (auto-calculated if not provided) */
  width?: number
  /** Whether to display the text portion of the logo */
  showText?: boolean
}

/**
 * BNSN Logo Component Implementation
 *
 * Renders the BNSN brand logo with intelligent responsive behavior.
 * When text is hidden, the component automatically adjusts dimensions
 * to show only the brain icon portion.
 *
 * @param props - Component props including styling and display options
 * @returns JSX.Element - The rendered SVG logo
 */
export function BnsnLogoColorWithText({
  className,
  height = 30,
  width,
  showText = true,
  ...props
}: BnsnLogoColorWithTextProps): React.JSX.Element {
  /** Original SVG dimensions for aspect ratio calculation */
  const originalWidth = 466.71
  const originalHeight = 119.74
  const aspectRatio = originalWidth / originalHeight

  /** Calculate final width based on text visibility - square when text hidden, aspect ratio maintained when shown */
  const finalWidth = showText ? (width ?? height * aspectRatio) : height
  /** Adjust viewBox to crop appropriately based on text visibility */
  const viewBox = showText ? `0 0 ${originalWidth} ${originalHeight}` : `0 0 ${originalHeight} ${originalHeight}`

  return (
    <svg
      {...props}
      className={className}
      width={finalWidth}
      height={height}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="Layer_1-2" data-name="Layer 1">
        <g>
          <g>
            <path
              fill={BRAND_BLUE}
              d="M89.91,64.89l-15.23-10.87c-8.09-5.77-12.91-15.14-12.91-25.08V10.38c0-3.35,1.55-6.41,4.26-8.38,2.7-1.97,6.08-2.52,9.26-1.49,14.98,4.81,27.5,15.03,35.25,28.78,4.79,8.49,2.4,19.17-5.56,24.84l-15.08,10.76h.01ZM72.11,8.17c-.58,0-1.03.25-1.27.43-.34.25-.91.81-.91,1.78v18.56c0,7.3,3.55,14.19,9.49,18.43l10.49,7.48,10.33-7.37c4.54-3.24,5.91-9.34,3.18-14.18-6.74-11.95-17.62-20.84-30.63-25.02-.24-.08-.47-.11-.68-.11Z"
            />
            <path
              fill={BRAND_BLUE}
              d="M72.1,119.74c-2.15,0-4.26-.68-6.07-2-2.71-1.98-4.26-5.03-4.26-8.38v-18.56c0-9.93,4.83-19.31,12.91-25.07l15.23-10.87,15.08,10.76c7.95,5.67,10.34,16.35,5.56,24.84h0c-7.75,13.75-20.27,23.97-35.25,28.78-1.05.34-2.13.5-3.19.5h0ZM89.91,64.89l-10.49,7.48c-5.94,4.24-9.49,11.13-9.49,18.42v18.56c0,.98.57,1.54.91,1.78.34.24,1.04.61,1.95.32,13.01-4.18,23.89-13.06,30.63-25.02,2.73-4.84,1.36-10.94-3.18-14.18l-10.33-7.38v.02Z"
            />
            <path
              fill={BRAND_BLUE}
              d="M40.9,119.74c-1.06,0-2.14-.17-3.19-.51-14.98-4.81-27.5-15.03-35.25-28.78-4.79-8.49-2.4-19.17,5.56-24.84l15.08-10.76,15.23,10.87c8.09,5.77,12.91,15.14,12.91,25.08v18.56c0,3.35-1.55,6.41-4.26,8.38-1.81,1.32-3.92,2-6.07,2h0ZM23.09,64.89l-10.33,7.37c-4.54,3.24-5.91,9.34-3.18,14.18,6.74,11.95,17.62,20.84,30.63,25.02.92.29,1.62-.07,1.95-.32.34-.25.91-.81.91-1.78v-18.56c0-7.3-3.55-14.19-9.49-18.43,0,0-10.49-7.48-10.49-7.48Z"
            />
            <path
              fill={BRAND_BLUE}
              d="M23.09,64.89l-15.08-10.76C.06,48.46-2.33,37.78,2.45,29.29h0C10.2,15.54,22.72,5.32,37.7.51c3.18-1.02,6.56-.48,9.26,1.49,2.71,1.98,4.26,5.03,4.26,8.38v18.56c0,9.93-4.83,19.31-12.91,25.07l-15.23,10.87h0ZM9.57,33.3h0c-2.73,4.84-1.36,10.94,3.18,14.18l10.33,7.37,10.49-7.48c5.94-4.24,9.49-11.13,9.49-18.42V10.39c0-.98-.57-1.54-.91-1.78-.34-.24-1.03-.61-1.95-.32-13.01,4.18-23.89,13.06-30.63,25.02h0Z"
            />
          </g>
          <g>
            <path
              fill={BRAND_DARK}
              d="M139.75,100.58V16.03h36.47c9.42,0,16.73,1.89,21.92,5.68s7.79,9.14,7.79,16.06c0,4.59-1.07,8.46-3.2,11.6-2.13,3.14-5.03,5.52-8.7,7.13-3.66,1.61-7.67,2.41-12.02,2.41l2.05-2.9c5.39,0,10.04.83,13.95,2.48,3.9,1.65,6.95,4.11,9.12,7.37s3.26,7.31,3.26,12.14c0,7.17-2.64,12.72-7.91,16.67s-13.23,5.92-23.85,5.92h-38.89.01ZM148.69,93.21h29.83c7.41,0,13.08-1.27,17.03-3.8,3.94-2.54,5.92-6.58,5.92-12.14s-1.97-9.48-5.92-12.02c-3.95-2.54-9.62-3.8-17.03-3.8h-30.8v-7.37h27.9c6.76,0,12.02-1.29,15.76-3.86,3.74-2.58,5.62-6.4,5.62-11.48s-1.87-9-5.62-11.53c-3.74-2.54-9-3.8-15.76-3.8h-26.93v69.81h0Z"
            />
            <path
              fill={BRAND_DARK}
              d="M228.4,100.58V16.03h7.37l58.34,73.43h-3.86V16.03h8.82v84.55h-7.37l-58.21-73.43h3.86v73.43h-8.95Z"
            />
            <path
              fill={BRAND_DARK}
              d="M347.49,101.3c-6.28,0-12.3-1.03-18.06-3.08s-10.21-4.69-13.35-7.91l3.5-6.88c2.98,2.9,6.98,5.33,12.02,7.31,5.03,1.98,10.33,2.96,15.88,2.96s9.64-.67,12.98-1.99c3.34-1.33,5.8-3.12,7.37-5.37s2.36-4.75,2.36-7.49c0-3.3-.95-5.96-2.84-7.97-1.89-2.01-4.37-3.6-7.43-4.77s-6.44-2.19-10.15-3.08c-3.7-.88-7.41-1.83-11.11-2.84-3.7-1.01-7.11-2.35-10.2-4.05-3.1-1.69-5.6-3.93-7.49-6.7-1.89-2.78-2.84-6.42-2.84-10.93,0-4.19,1.11-8.03,3.32-11.53,2.21-3.5,5.62-6.32,10.21-8.46,4.59-2.13,10.47-3.2,17.63-3.2,4.75,0,9.46.67,14.13,1.99,4.67,1.33,8.7,3.16,12.08,5.5l-3.02,7.13c-3.62-2.41-7.47-4.19-11.54-5.31-4.07-1.13-7.99-1.69-11.78-1.69-5.07,0-9.26.69-12.56,2.05-3.3,1.37-5.74,3.2-7.31,5.5s-2.35,4.89-2.35,7.79c0,3.3.94,5.96,2.84,7.97,1.89,2.01,4.39,3.58,7.49,4.71,3.1,1.13,6.5,2.13,10.21,3.02,3.7.89,7.39,1.85,11.05,2.9,3.66,1.05,7.04,2.4,10.15,4.05,3.1,1.65,5.6,3.85,7.49,6.58,1.89,2.74,2.84,6.32,2.84,10.75,0,4.11-1.13,7.91-3.38,11.41s-5.7,6.32-10.33,8.45c-4.63,2.13-10.57,3.2-17.81,3.2v-.02Z"
            />
            <path
              fill={BRAND_DARK}
              d="M396.04,100.58V16.03h7.37l58.34,73.43h-3.86V16.03h8.82v84.55h-7.37l-58.21-73.43h3.86v73.43h-8.95Z"
            />
          </g>
        </g>
      </g>
    </svg>
  )
}
